import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { useStaffSearch } from "../hooks/useStaff";
import { useDebounce } from "@/hooks/useDebounce";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";

interface UserMultiSelectProps {
  selectedUserIds: string[];
  onSelectionChange: (userIds: string[]) => void;
}

export const UserMultiSelect: React.FC<UserMultiSelectProps> = ({
  selectedUserIds,
  onSelectionChange,
}) => {
  const hotelId = useCurrentHotelId();

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Fetch users based on search term
  const { data: searchResults, isLoading: isSearchLoading } = useStaffSearch(
    {
      name: debouncedSearchTerm,
      hotelId: hotelId ?? undefined,
    },
    {
      enabled: !!debouncedSearchTerm,
    },
  );

  const handleSelect = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onSelectionChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      onSelectionChange([...selectedUserIds, userId]);
    }
  };

  const allUsers = useMemo(() => [...(searchResults || [])], [searchResults]);

  const isLoading = isSearchLoading;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="new_outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          {selectedUserIds.length > 0
            ? `${selectedUserIds.length} selected`
            : "Select users..."}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <div className="border-b p-2">
          <div className="flex items-center rounded-md border px-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 border-0 px-0 focus-visible:ring-0"
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          {isLoading && (
            <div className="text-muted-foreground p-2 text-center text-sm">
              Loading...
            </div>
          )}
          {!isLoading && allUsers.length === 0 && (
            <div className="text-muted-foreground p-2 text-center text-sm">
              No users found.
            </div>
          )}
          {!isLoading && allUsers.length !== 0 && (
            <div className="space-y-1">
              {allUsers.map((user) => (
                <div
                  key={user.id}
                  className={cn(
                    "hover:bg-accent hover:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                    selectedUserIds.includes(user.id) && "bg-accent",
                  )}
                  onClick={() => handleSelect(user.id)}
                >
                  <div
                    className={cn(
                      "border-primary flex h-4 w-4 items-center justify-center rounded-sm border",
                      selectedUserIds.includes(user.id)
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible",
                    )}
                  >
                    <Check className={cn("h-3 w-3")} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {user.firstName} {user.lastName}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {user.email}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {selectedUserIds.length > 0 && (
          <div className="bg-muted/50 border-t p-2">
            <Button
              variant="new_ghost"
              size="sm"
              className="h-8 w-full"
              onClick={() => onSelectionChange([])}
            >
              Clear selection
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
